import { z } from "zod";
import { createEndpoint, createEndpointWithNumberedPagination, createEndpointWithPagination } from "../createEndpoint";
import { ReviewSchema } from "../schemas/reviews";
import {
    ExpertCustomerStorySchema,
    ExpertDetailsSchema,
    ExpertPackagedServiceSchema,
} from "../schemas/expert-details";
import { ExpertCardDetailsSchema } from "../schemas/expert-card-details";
import { CustomerStoryDetailsSchema } from "../schemas/customer-story-details";
import { PublicCustomerStoryDetailsSchema } from "../schemas/customer-story-public";
import { PackagedServiceDetailsSchema } from "../schemas/packaged-service-details";
import { ExpertListingMetaSchema } from "../schemas/expert-listing-meta";
import { FacetsResponseSchema } from "../schemas/facets";
import { PublicExpertMinimalResponseSchema } from "../schemas/expert-minimal";
import {
    ExpertConfidenceSignalsSchema,
    ExpertPartnerReviewsSchema,
    ExpertPublicProfileSchema,
} from "../schemas/expert-public-profile";
import type { HttpClient, QueryParams } from "../httpClient";
import { PublicReviewSchema, PublicReviewsStatsSchema } from "../schemas/reviews-v2";
import type { ShopifyPartnerTier } from "../schemas";
import { PublicExpertListingCardSchema, PublicExpertListingMetaSchema } from "../schemas/public-expert-listing";

export interface ExpertListingQueryParams {
    expert_type?: 'freelancer' | 'agency' | 'app_service_provider';
    services_offered?: string[];
    starting_price?: {
        from?: number;
        to?: number;
    };
    countries?: string[];
    /** GeoNames city ids, see the cities resource / `cities` facet. */
    cities?: number[];
    languages?: string[];
    apps_and_themes?: string[];
    partner_tiers?: ShopifyPartnerTier[];
    partner_badge?: string;
    search?: string;
    is_pro?: boolean;
    claimed_only?: boolean;
    ranking?: 'all_time' | 'most_recent';
}

export interface ExpertsBySlugsQueryParams extends QueryParams {
    slugs: string[];
}

export interface FacetsQueryParams extends QueryParams {
    // JSON stringified array of { value: string, label: string } options
    // e.g. '[{"value":"50-100","label":"$50 - $100"},...]'
    // Labels are defined by frontend; backend returns only available options with the same labels
    starting_price_options?: string;
}

export interface ExpertMinimalQueryParams extends QueryParams {
    /** Optional packaged service slug to include in response */
    packaged_service_slug?: string;
}

export interface ExpertProfileReviewsQueryParams extends QueryParams {
    /** Exact displayed star bucket. Must be repeated with cursor requests. */
    rating?: 1 | 2 | 3 | 4 | 5;
}

function flattenQueryParams(params?: ExpertListingQueryParams): QueryParams | undefined {
    if (!params) return undefined;
    
    const flattened: QueryParams = {};
    
    // Copy regular string/number parameters
    if (params.expert_type) flattened.expert_type = params.expert_type;
    if (params.search) flattened.search = params.search;
    if (params.is_pro !== undefined) flattened.is_pro = params.is_pro;
    if (params.claimed_only !== undefined) flattened.claimed_only = params.claimed_only;
    if (params.ranking) flattened.ranking = params.ranking;
    
    // Convert arrays to comma-separated strings
    if (params.services_offered && params.services_offered.length > 0) {
        flattened.services_offered = params.services_offered.join(',');
    }
    if (params.countries && params.countries.length > 0) {
        flattened.countries = params.countries.join(',');
    }
    if (params.cities && params.cities.length > 0) {
        flattened.cities = params.cities.join(',');
    }
    if (params.languages && params.languages.length > 0) {
        flattened.languages = params.languages.join(',');
    }
    if (params.apps_and_themes && params.apps_and_themes.length > 0) {
        flattened.apps_and_themes = params.apps_and_themes.join(',');
    }
    if (params.partner_tiers && params.partner_tiers.length > 0) {
        flattened.partner_tiers = params.partner_tiers.join(',');
    }
    if (params.partner_badge) {
        flattened.partner_badge = params.partner_badge;
    }
    
    // Flatten nested starting_price object
    if (params.starting_price) {
        if (params.starting_price.from !== undefined) {
            flattened['starting_price[from]'] = params.starting_price.from;
        }
        if (params.starting_price.to !== undefined) {
            flattened['starting_price[to]'] = params.starting_price.to;
        }
    }
    
    return flattened;
}

export function createExpertPublicApi(http: HttpClient, baseUrl: string) {
    const expertBaseUrl = `${baseUrl}/expert`;
    const publicBaseUrl = `${baseUrl}/public/experts`;
    const customerStoriesBaseUrl = `${baseUrl}/customer-stories`;
    return {
        /** @deprecated Use api.public.reviewsV2.getExpertReviews instead */
        getReviews: createEndpointWithPagination(http, 'GET', '/:slug/reviews', {
            base: expertBaseUrl,
            itemSchema: ReviewSchema,
            metaSchema: PublicReviewsStatsSchema,
            params: ['slug'],
        }),
        getDetails: createEndpoint(http, 'GET', '/:slug/details', {
            base: expertBaseUrl,
            responseSchema: ExpertDetailsSchema,
            params: ['slug'],
        }),
        /** Public, published review and its latest generated share image. */
        getReviewShare: createEndpoint(http, 'GET', '/:slug/review-shares/:reviewId', {
            base: `${baseUrl}/public/expert-profiles`,
            params: ['slug', 'reviewId'],
            responseSchema: z.object({
                review: PublicReviewSchema,
                image_url: z.string().url(),
                version: z.string(),
                expires_at: z.string(),
            }),
        }),
        /** Consolidated profile plus the first page of every profile section. */
        getProfile: createEndpoint(http, 'GET', '/:slug', {
            base: `${baseUrl}/expert-profiles`,
            responseSchema: ExpertPublicProfileSchema,
            params: ['slug'],
        }),
        getProfileCaseStudies: createEndpointWithPagination(http, 'GET', '/:slug/case-studies', {
            base: `${baseUrl}/expert-profiles`,
            itemSchema: ExpertCustomerStorySchema,
            params: ['slug'],
        }),
        getProfilePackagedServices: createEndpointWithPagination(http, 'GET', '/:slug/packaged-services', {
            base: `${baseUrl}/expert-profiles`,
            itemSchema: ExpertPackagedServiceSchema,
            params: ['slug'],
        }),
        getProfileReviews: createEndpointWithPagination<
            z.infer<typeof PublicReviewSchema>,
            z.infer<typeof PublicReviewsStatsSchema>,
            unknown,
            ExpertProfileReviewsQueryParams,
            { slug: string }
        >(http, 'GET', '/:slug/reviews', {
            base: `${baseUrl}/expert-profiles`,
            itemSchema: PublicReviewSchema,
            metaSchema: PublicReviewsStatsSchema,
            params: ['slug'],
        }),
        /**
         * All published partner-directory (Shopify) reviews for an expert.
         * GET /api/v2/expert/:slug/partner-reviews
         */
        getPartnerReviews: createEndpoint(http, 'GET', '/:slug/partner-reviews', {
            base: expertBaseUrl,
            responseSchema: ExpertPartnerReviewsSchema,
            params: ['slug'],
        }),
        /**
         * Public confidence signals (trust/verification breakdown).
         * GET /api/v2/experts/:slug/confidence-signals
         */
        getConfidenceSignals: createEndpoint(http, 'GET', '/:slug/confidence-signals', {
            base: `${baseUrl}/experts`,
            responseSchema: ExpertConfidenceSignalsSchema,
            params: ['slug'],
        }),
        /**
         * Get minimal expert details with optional packaged service
         * GET /api/v2/public/experts/:slug/minimal
         * 
         * @param slug - Expert slug
         * @param packaged_service_slug - Optional packaged service slug
         */
        getMinimalExpert: createEndpoint<
            z.infer<typeof PublicExpertMinimalResponseSchema>,
            unknown,
            ExpertMinimalQueryParams,
            { slug: string }
        >(http, 'GET', '/:slug/minimal', {
            base: publicBaseUrl,
            responseSchema: PublicExpertMinimalResponseSchema,
            params: ['slug'],
        }),
        getCustomerStory: createEndpoint(http, 'GET', '/:slug/customer-stories/:storySlug', {
            base: expertBaseUrl,
            responseSchema: CustomerStoryDetailsSchema,
            params: ['slug', 'storySlug'],
        }),
        getCustomerStoryBySlug: createEndpoint(http, 'GET', '/:storySlug', {
            base: customerStoriesBaseUrl,
            responseSchema: PublicCustomerStoryDetailsSchema,
            params: ['storySlug'],
        }),
        getPackagedService: createEndpoint(http, 'GET', '/:slug/packaged-services/:serviceSlug', {
            base: expertBaseUrl,
            responseSchema: PackagedServiceDetailsSchema,
            params: ['slug', 'serviceSlug'],
        }),
        getExpertListing: async (args?: {
            query?: ExpertListingQueryParams;
            cursor?: string;
            options?: any;
        }) => {
            const endpoint = createEndpointWithPagination(http, 'GET', '/experts-profile-listing', {
                base: baseUrl,
                itemSchema: ExpertCardDetailsSchema,
                metaSchema: ExpertListingMetaSchema,
            });
            
            const flattenedQuery = flattenQueryParams(args?.query);
            
            return endpoint({
                query: flattenedQuery,
                cursor: args?.cursor,
                options: args?.options,
            });
        },
        getExpertListingPaginated: async (args?: {
            query?: ExpertListingQueryParams;
            page?: number;
            perPage?: number;
            options?: any;
        }) => {
            const endpoint = createEndpointWithNumberedPagination(http, 'GET', '/public/experts', {
                base: baseUrl,
                itemSchema: ExpertCardDetailsSchema,
                metaSchema: ExpertListingMetaSchema,
            });

            let flattenedQuery = flattenQueryParams(args?.query);
            if (args?.perPage != null) {
                flattenedQuery ??= {};
                flattenedQuery.per_page = args.perPage;
            }

            return endpoint({
                query: flattenedQuery,
                page: args?.page,
                options: args?.options,
            });
        },
        getPublicExpertListings: async (args?: {
            query?: ExpertListingQueryParams;
            page?: number;
            perPage?: number;
            options?: any;
        }) => {
            const endpoint = createEndpointWithNumberedPagination(http, 'GET', '/public/expert-listings', {
                base: baseUrl,
                itemSchema: PublicExpertListingCardSchema,
                metaSchema: PublicExpertListingMetaSchema,
            });

            let flattenedQuery = flattenQueryParams(args?.query);
            if (args?.perPage != null) {
                flattenedQuery ??= {};
                flattenedQuery.per_page = args.perPage;
            }

            return endpoint({
                query: flattenedQuery,
                page: args?.page,
                options: args?.options,
            });
        },
        getFacets: createEndpoint<
            z.infer<typeof FacetsResponseSchema>,
            unknown,
            FacetsQueryParams
        >(http, 'GET', '/experts-profile-listing/facets', {
            base: baseUrl,
            responseSchema: FacetsResponseSchema,
        }),
        getExpertsBySlugs: createEndpoint<
            z.infer<typeof ExpertCardDetailsSchema>[],
            unknown,
            ExpertsBySlugsQueryParams
        >(http, 'GET', '/experts-profile-listing/by-slugs', {
            base: baseUrl,
            responseSchema: z.array(ExpertCardDetailsSchema),
        }),
    }
}
